/**
 * PlatformTenantDetailView — `/platform/clients/$tenantId` (OT-0073 §4.3).
 *
 * Datos del cliente, su administrador (con "Reenviar invitación"), saldo,
 * historial de créditos e historial de estado, y las acciones de ciclo de
 * vida. Los botones que se muestran siguen el estado del cliente para no
 * ofrecer transiciones imposibles; la regla la aplica el backend.
 */

import { useState } from 'react'
import { Link, useParams } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { isNotFound } from '../../api/client'
import type { PlatformTenantDetail } from '../../api/platform'
import { ClientStateBadge, TrialEnd } from '../../components/platform/clientDisplay'
import {
  TenantActionDialog,
  type TenantActionKind,
} from '../../components/platform/TenantActionDialog'
import { ScrollPage } from '../../components/shell/ScrollPage'
import { formatDateTime, formatUsd } from '../../components/usage/usageHelpers'
import { useApiErrorMessage } from '../../hooks/useApiErrorMessage'
import {
  usePlatformTenantDetail,
  useResendPlatformTenantAdminInvite,
} from '../../hooks/usePlatform'
import { formatDay } from '../../lib/organizationState'

export function PlatformTenantDetailView() {
  const { tenantId } = useParams({ from: '/platform/clients/$tenantId' })
  const { t } = useTranslation(['platform', 'common'])
  const errorMessage = useApiErrorMessage()
  const detail = usePlatformTenantDetail(tenantId)

  return (
    <ScrollPage>
      <div className="max-w-5xl">
        <Link
          to="/platform/clients"
          className="mb-4 inline-flex items-center gap-1 text-sm text-ink-muted hover:text-brand"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          {t('clients.detail.back')}
        </Link>
        {detail.isPending ? (
          <p className="text-sm text-ink-muted">{t('common:actions.loading')}</p>
        ) : detail.error ? (
          <p role="alert" className="text-sm text-critical">
            {isNotFound(detail.error) ? t('clients.detail.not_found') : errorMessage(detail.error)}
          </p>
        ) : (
          <ClientDetail client={detail.data} />
        )}
      </div>
    </ScrollPage>
  )
}

function ClientDetail({ client }: { client: PlatformTenantDetail }) {
  const { t, i18n } = useTranslation(['platform', 'common'])
  const locale = i18n.language
  const [action, setAction] = useState<TenantActionKind | null>(null)

  const actions: TenantActionKind[] = []
  if (client.status === 'trial') actions.push('extend_trial')
  actions.push('grant_credit')
  if (client.status === 'suspended') actions.push('reactivate')
  else if (!client.is_platform_operator) actions.push('suspend')
  if (client.status === 'trial') actions.push('convert_to_active')

  return (
    <>
      <header className="mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-display text-2xl font-semibold text-ink">{client.display_name}</h1>
          <ClientStateBadge client={client} />
          {client.is_platform_operator ? (
            <span className="rounded bg-surface-muted px-2 py-0.5 text-xs text-ink-muted">
              {t('clients.operator_badge')}
            </span>
          ) : null}
        </div>
        <p className="mt-1 font-mono text-xs text-ink-subtle">{client.slug}</p>
      </header>

      <div className="grid gap-6 md:grid-cols-2">
        <Section title={t('clients.detail.data')}>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
            <Field label={t('clients.detail.trial_end')}>
              {client.trial_ends_at ? (
                <TrialEnd trialEndsAt={client.trial_ends_at} locale={locale} />
              ) : (
                <span className="text-ink-subtle">{t('clients.detail.no_trial_end')}</span>
              )}
            </Field>
            <Field label={t('clients.detail.balance')}>
              <span className="font-mono">{formatUsd(client.balance_usd, locale)}</span>
            </Field>
            <Field label={t('clients.detail.members')}>
              <span className="font-mono">{client.members}</span>
            </Field>
            <Field label={t('clients.detail.last_activity')}>
              {formatDateTime(client.last_activity_at, locale)}
            </Field>
            <Field label={t('clients.detail.plan_tier')}>
              {t(`clients.plan_tier.${client.plan_tier}`)}
            </Field>
            <Field label={t('clients.detail.default_locale')}>
              {t(`common:languages.${client.default_locale}`, client.default_locale)}
            </Field>
            <Field label={t('clients.detail.created_at')}>{formatDay(client.created_at, locale)}</Field>
          </dl>
        </Section>

        <AdminSection client={client} />
      </div>

      <Section title={t('clients.detail.actions')} className="mt-6">
        {client.is_platform_operator ? (
          <p className="mb-3 text-sm text-ink-muted">{t('clients.detail.operator_notice')}</p>
        ) : null}
        <div className="flex flex-wrap gap-2">
          {actions.map((kind) => (
            <button
              key={kind}
              type="button"
              onClick={() => setAction(kind)}
              className="rounded-md border border-line bg-surface px-3 py-1.5 text-sm text-ink hover:bg-surface-muted"
            >
              {t(`clients.action.${kind}.button`)}
            </button>
          ))}
        </div>
      </Section>

      <CreditHistory client={client} />
      <LifecycleHistory client={client} />

      <TenantActionDialog
        tenantId={client.tenant_id}
        slug={client.slug}
        kind={action}
        onClose={() => setAction(null)}
      />
    </>
  )
}

function AdminSection({ client }: { client: PlatformTenantDetail }) {
  const { t, i18n } = useTranslation('platform')
  const errorMessage = useApiErrorMessage()
  const resend = useResendPlatformTenantAdminInvite(client.tenant_id)
  const pendingInvite = client.admin_state !== 'active' && client.admin_email !== null

  return (
    <Section title={t('clients.detail.admin')}>
      <p className="font-mono text-sm text-ink">
        {client.admin_email ?? t('clients.detail.no_admin_email')}
      </p>
      <p className="mt-1 text-sm text-ink-muted">{t(`clients.admin_state.${client.admin_state}`)}</p>
      {client.admin_state === 'invited' && client.admin_invite_expires_at ? (
        <p className="mt-1 text-xs text-ink-subtle">
          {t('clients.detail.admin_invite_expires', {
            date: formatDay(client.admin_invite_expires_at, i18n.language),
          })}
        </p>
      ) : null}
      {pendingInvite ? (
        <button
          type="button"
          disabled={resend.isPending}
          onClick={() => resend.mutate()}
          className="mt-3 rounded-md border border-line bg-surface px-3 py-1.5 text-sm text-ink hover:bg-surface-muted disabled:opacity-50"
        >
          {t('clients.detail.resend_invite')}
        </button>
      ) : null}
      {resend.error ? (
        <p role="alert" className="mt-2 text-sm text-critical">
          {errorMessage(resend.error)}
        </p>
      ) : null}
    </Section>
  )
}

function CreditHistory({ client }: { client: PlatformTenantDetail }) {
  const { t, i18n } = useTranslation('platform')
  const locale = i18n.language
  return (
    <Section title={t('clients.detail.credits')} className="mt-6">
      {client.credits.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('clients.detail.credits_empty')}</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-line">
          <table className="w-full text-sm">
            <thead className="bg-surface-muted text-[11px] uppercase tracking-wider text-ink-subtle">
              <tr>
                <th className="px-3 py-2 text-left font-medium">{t('clients.detail.credit_col.date')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('clients.detail.credit_col.amount')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('clients.detail.credit_col.notes')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('clients.detail.credit_col.by')}</th>
              </tr>
            </thead>
            <tbody>
              {client.credits.map((c, i) => (
                <tr key={`${c.created_at}-${i}`} className="border-t border-line">
                  <td className="px-3 py-2 text-ink-muted">{formatDateTime(c.created_at, locale)}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatUsd(c.amount_usd, locale)}</td>
                  <td className="px-3 py-2 text-ink">{c.notes ?? '—'}</td>
                  <td className="px-3 py-2 text-ink-muted">{c.created_by ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  )
}

function LifecycleHistory({ client }: { client: PlatformTenantDetail }) {
  const { t, i18n } = useTranslation('platform')
  const locale = i18n.language
  return (
    <Section title={t('clients.detail.lifecycle')} className="mt-6">
      {client.lifecycle.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('clients.detail.lifecycle_empty')}</p>
      ) : (
        <ol className="space-y-3">
          {client.lifecycle.map((e, i) => {
            const reason = typeof e.details.reason === 'string' ? e.details.reason : null
            return (
              <li key={`${e.occurred_at}-${i}`} className="border-l-2 border-line pl-3">
                <p className="text-sm text-ink">
                  <span className="font-medium">
                    {t(`clients.event.${e.event_type}`, e.event_type)}
                  </span>
                  {e.actor ? (
                    <span className="text-ink-muted">
                      {' '}
                      {t('clients.detail.lifecycle_by', { actor: e.actor })}
                    </span>
                  ) : null}
                </p>
                <p className="text-xs text-ink-subtle">{formatDateTime(e.occurred_at, locale)}</p>
                {reason ? (
                  <p className="mt-1 text-sm text-ink-muted">
                    {t('clients.detail.lifecycle_reason', { reason })}
                  </p>
                ) : null}
              </li>
            )
          })}
        </ol>
      )}
    </Section>
  )
}

function Section({
  title,
  className,
  children,
}: {
  title: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <section className={`rounded-md border border-line bg-surface p-4 ${className ?? ''}`}>
      <h2 className="mb-3 font-display text-lg text-ink">{title}</h2>
      {children}
    </section>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-ink-subtle">{label}</dt>
      <dd className="text-ink">{children}</dd>
    </>
  )
}
